// Scan a Guv POP Tag from inside the engineer app (Session AND). "POP" is Proof Of Presence; the
// feature was called "Tapping For Tracking" through Session AMZ.
//
// WHY THIS SCREEN EXISTS. Session AMZ made the motor-room tag prove WHEN a lift was attended. It
// proved WHO only by accident: the sticker's URL is opened by the phone's OS in whatever browser it
// prefers, and that browser's cookie jar need not be the installed PWA's, so the recorded row was
// often nameless and nobody noticed. Reading the same sticker from in here makes a live session a
// PRECONDITION of the read rather than a coincidence of it.
//
// HOW THE GUARANTEE IS ACTUALLY DELIVERED, in two halves, because neither half is enough alone:
//
//   PREVENTION. Pressing Scan does a real `api.me()` round trip FIRST, and the scan does not start
//   unless it comes back with a staff role. This is a live server confirmation of the very cookie
//   the POST will carry, milliseconds before it carries it.
//
//   DETECTION. On a FRESH tap the route sets `signedInAs` from the actor it actually used, and
//   User.displayName is non-nullable, so a falsy `signedInAs` is exactly equivalent to the row
//   having been written with no user. The screen therefore reads back the row it just created rather
//   than trusting its own gate, and says plainly when the name did not land.
//
// AND THE HONEST LIMIT, which belongs at the top rather than in a footnote: this makes a POP Tag
// recorded THROUGH THIS SCREEN carry a name, or tell the engineer in the same breath that it did
// not. It does NOT make every AttendanceTap row carry a name. The sticker-opens-a-browser path is
// untouched and remains identity-optional by design, and the route still accepts an unauthenticated
// POST from anyone holding the token. The wet-signed log card is still the record of who attended.
//
// WHY THE SERVER WAS NOT MADE ABLE TO REFUSE. A 401 on a missing identity would destroy evidence: an
// engineer genuinely standing in the motor room with a dead cookie would leave ELE with NOTHING,
// where an anonymous row plus this screen's loud warning leaves "somebody was at this lift at 10:14"
// alongside a signed log card that names them. It would also buy no security at all, because the
// threat here is inattention rather than forgery: whoever programmed the sticker already holds the
// token and can POST from anywhere. See docs/HANDOFF-2026-09-04-AND.md.
//
// ANDROID ONLY, by explicit Director decision that the engineer fleet is Android. Web NFC is
// Chrome-on-Android only, there is no cross-platform fallback, and none should be invented: the
// existing tap-the-sticker path still works everywhere and is what an unsupported phone is told to
// use.
//
// SESSION APF: THE SCAN ITSELF NOW LIVES IN usePopTagScan.ts, and this screen is a thin consumer of
// it. The job sheet gained two "tap the tag" steps (PopTagStep.tsx) and a scan is not something to
// implement twice, so the whole state machine moved out, every one of its WHY comments with it: the
// preflight and its three outcomes, the controller created before the preflight, the listeners, the
// thirty second timeout, the abort on unmount. Nothing about what THIS screen does or says changed,
// and PopTagScanPage.test.tsx passing UNCHANGED is the standing proof of that. It hands the hook no
// body, so it goes on recording exactly the undirected tap it always has.
import { Link } from "react-router-dom";
import { POP_TAG_OUTCOME_COPY, popTagOutcomeIsClean } from "../../lib/popTag";
import { londonTime, usePopTagScan } from "./usePopTagScan";

export function PopTagScanPage() {
  const { phase, message, unconfirmed, result, outcome, scan, cancel, reset } = usePopTagScan();

  const busy = phase === "checking" || phase === "scanning" || phase === "posting";

  return (
    <div className="pb-8">
      <Link to="/engineer" className="mb-3 inline-flex min-h-11 min-w-11 items-center font-menu text-xs tracking-wider text-ele-green">
        &lsaquo; Home
      </Link>

      <h1 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">Scan POP Tag</h1>
      <p className="mb-4 text-sm text-ele-body">
        The Guv POP Tag is the small sticker on the paper log card in the motor room. Scanning it from in here records
        that you were at the lift, in your name. Please sign the log card as well, as usual.
      </p>

      {phase === "unsupported" ? (
        <div className="rounded-lg border border-ele-card-border bg-ele-card p-4">
          <p className="font-heading text-lg text-ele-strong">Scanning is not available on this phone</p>
          <p className="mt-1 text-sm text-ele-body">
            In-app scanning needs Chrome on Android. Tap the sticker with your phone in the usual way instead: it opens
            the same page and records the visit. Please sign the log card either way.
          </p>
        </div>
      ) : null}

      {phase !== "unsupported" && phase !== "done" ? (
        <div className="space-y-3">
          <button
            type="button"
            onClick={() => void scan()}
            disabled={busy}
            className="flex min-h-11 w-full items-center justify-center rounded-ele bg-ele-green px-4 font-menu text-sm tracking-wide text-ele-bg disabled:opacity-60"
          >
            {phase === "checking"
              ? "Checking you are signed in..."
              : phase === "scanning"
                ? "Waiting for the tag..."
                : phase === "posting"
                  ? "Recording..."
                  : "Scan POP Tag"}
          </button>

          {phase === "scanning" ? (
            <p className="text-sm text-ele-body" role="status">
              Hold the back of your phone against the sticker. Keep the screen on.
            </p>
          ) : null}

          {/* Offered during CHECKING as well as scanning. A motor room is the worst signal in the
              building, so a slow preflight is ordinary, and with the Scan button disabled there was
              otherwise nothing on this screen to press. Not offered during POSTING: the tag has been
              read and the row is being written, so there is nothing left to cancel. */}
          {phase === "checking" || phase === "scanning" ? (
            <button
              type="button"
              onClick={cancel}
              className="flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-card-border px-4 font-menu text-xs tracking-wide text-ele-muted"
            >
              Cancel
            </button>
          ) : null}
        </div>
      ) : null}

      {/* The preflight could not reach the server. Amber, because it is neither a success nor a
          refusal: the scan is going ahead and the result panel will say what actually landed. */}
      {unconfirmed && phase !== "done" ? (
        <p className="mt-3 rounded-lg border border-ele-card-border bg-ele-card p-3 text-sm" style={{ color: "var(--ele-amber-text)" }} role="status">
          Could not confirm you are signed in. The scan will go ahead, and you will be told whether your name reached
          the record.
        </p>
      ) : null}

      {message ? (
        <p className="mt-3 rounded-lg border border-ele-card-border bg-ele-card p-3 text-sm text-ele-body" role="alert">
          {message}
        </p>
      ) : null}

      {phase === "done" && result && outcome ? (
        <div className="mt-4 rounded-lg border border-ele-card-border bg-ele-card p-4">
          {/* Theme-aware, never raw hex. The engineer shell has a LIGHT theme ([data-theme="light"]
              .engineer-ui) in which --ele-card is #ffffff, so the dark-ground values these two lines
              used to carry (#63C41F as small text, and the cream amber ink #fbf3dd) were an AA
              failure and a near-invisible one respectively. text-ele-green resolves to
              --ele-green-ink and --ele-amber-text flips with the theme; both are the platform's own
              measured inks rather than a colour invented here. */}
          <p
            className={`font-heading text-sm uppercase tracking-widest ${popTagOutcomeIsClean(outcome) ? "text-ele-green" : ""}`}
            style={popTagOutcomeIsClean(outcome) ? undefined : { color: "var(--ele-amber-text)" }}
          >
            {POP_TAG_OUTCOME_COPY[outcome].heading}
          </p>
          <h2 className="mt-2 font-heading text-2xl text-ele-strong">{result.assetRef}</h2>
          <p className={`mt-1 font-heading text-3xl ${popTagOutcomeIsClean(outcome) ? "text-ele-green" : "text-ele-strong"}`}>
            {londonTime(result.tappedAt)}
          </p>
          <p className="mt-0.5 text-sm text-ele-body opacity-80">{londonDate(result.tappedAt)}</p>

          {result.signedInAs ? (
            <p className="mt-3 text-sm text-ele-body">
              Recorded against <span className="text-ele-strong">{result.signedInAs}</span>.
            </p>
          ) : null}

          <p className="mt-3 text-sm text-ele-body">{POP_TAG_OUTCOME_COPY[outcome].body}</p>

          <button
            type="button"
            onClick={reset}
            className="mt-4 flex min-h-11 w-full items-center justify-center rounded-ele border border-ele-card-border px-4 font-menu text-xs tracking-wide text-ele-muted"
          >
            Scan another tag
          </button>
        </div>
      ) : null}
    </div>
  );
}

// The recorded DATE as London wall clock, for the same reason the time beside it is (londonTime,
// which moved to usePopTagScan.ts in Session APF so the job sheet's two tap steps render a tap's
// time the way this screen does): a UTC render would read a day early for an hour every summer night.
function londonDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "Europe/London",
  });
}

export default PopTagScanPage;
